import dayjs from "dayjs";

import { Category } from "../../backend/generated/category-model";
import { CostCalculationPercentage } from "../../backend/generated/cost-calculation-percentage-model";
import { CreateCostCalculationPercentageInput } from "../../backend/generated/create-cost-calculation-percentage-input";
import { CreateSaleInput, SaleCoreProperties } from "../../backend/generated/create-sale-input";
import { GetSaleInput } from "../../backend/generated/get-sale-input";
import { Property } from "../../backend/generated/property-model";
import { Sale } from "../../backend/generated/sale-model";
import { maxScoreInvestmentClass } from "../../backend/types/user-scoring";
import { AssetTypes, CostCalculationPercentageType, InvestmentClass, PropertyType, ValidationMessages } from "../../backend/types/utils";
import { randomAdjective, randomBoolean, randomDate, randomNoun, randomNumber, randomSelectOption, randomString } from "../../utils/random";
import { isEmptyObject, prune } from "../../utils/utility";
import { CustomValidationArguments, validateDates, validateNumbersInRange, validateRequiredNotDefault } from "../../utils/validation";
import { CreateFileManageable } from "../file/types";
import { CreatePropertyManageable } from "../property/types";

export class SaleManageable extends Sale {
  constructor(obj: any) {
    super();

    obj && Object.assign(
      this,
      prune(obj),
      {
        assetType: obj.assetType ? obj.assetType.toLowerCase() as AssetTypes : obj.assetType,
        investmentClass: obj.investmentClass ? obj.investmentClass.toLowerCase() as InvestmentClass : obj.investmentClass,
        percentages: obj.percentages ? obj.percentages.map((percentage: CostCalculationPercentage) => ({
          ...percentage,
          type: percentage.type.toLowerCase(),
        })) : obj.percentages,
        corePropertyRepresentations: obj.corePropertyRepresentations ? obj.corePropertyRepresentations.map((category: Category) => ({
          ...category,
          properties: [...category.properties]
        })) : obj.corePropertyRepresentations,
        properties: obj.properties ? obj.properties.map((property: Property) => ({
          ...property,
          type: property.type.toLowerCase(),
        })) : obj.property,
        status: obj.status ? obj.status.toLowerCase() : obj.status,
      }
    );

    const investments = [];
    if (this.investments) {
      for (let i = 0; i < this.investments.length; i++) {
        investments.push(this.investments[i]);
      }
    }
    this.investments = investments;
  }

  get percentageFunded(): number {
    const factor = 100;
    return Math.round((100 - this.sharesAvailable * 100 / this.platformAssetValue) * factor) / factor;
  }

  get sharesReserved(): number {
    return this.sharesSold + this.sharesLocked;
  }

  get sharesAvailable(): number {
    return this.platformAssetValue - this.sharesReserved;
  }

  static default(): any {
    return { ...new Sale() }
  }
}

export class CreateCostCalculationPercentageInputExtended extends CreateCostCalculationPercentageInput {
  static readonly minPercentage: number = 0;
  static readonly maxPercentage: number = 100;
}

export class CreateCostCalculationPercentageManageable extends CreateCostCalculationPercentageInputExtended {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        CreateCostCalculationPercentageManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        CreateCostCalculationPercentageManageable.default()
      );
    }
  }

  static readonly required = { type: true };

  static default(): any {
    return {
      ...new CreateCostCalculationPercentageInput(),
      type: "",
      main: "",
      annual: "",
    }
  }

  automatic(): any {
    const main = randomNumber(
      CreateCostCalculationPercentageManageable.minPercentage,
      50
    );

    const annual = randomNumber(
      CreateCostCalculationPercentageManageable.minPercentage,
      10
    );

    return {
      ...this,
      main,
      annual,
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      CreateSaleManageable.required,
      this,
      CreateSaleManageable.default(),
      keys
    );

    errors = Object.assign(
      errors,
      validateNumbersInRange(
        [{
          key: 'main',
          min: CreateCostCalculationPercentageManageable.minPercentage,
          max: CreateCostCalculationPercentageManageable.maxPercentage,
        }, {
          key: 'annual',
          min: CreateCostCalculationPercentageManageable.minPercentage,
          max: CreateCostCalculationPercentageManageable.maxPercentage,
        }],
        errors,
        this,
        keys,
        messages[ValidationMessages.MINIMUM],
        messages[ValidationMessages.MAXIMUM],
      )
    );

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    data.type = data.type.toUpperCase();

    if (data.main) {
      data.main = parseFloat(data.main);
    } else {
      delete data.main;
    }

    if (data.annual) {
      data.annual = parseFloat(data.annual);
    } else {
      delete data.annual;
    }

    return data;
  }
}

export class CreateSaleManageable extends CreateSaleInput {
  static readonly maxPercentage: number = 100;
  static readonly maxScoreCustomerGroup: number = 3;
  static readonly maxScoreWPServices: number = 7;

  static readonly minFee: number = 1;
  static readonly minInterestRate: number = 1;
  static readonly minMinPurchase: number = 1;
  static readonly minMaxPurchase: number = 1;
  static readonly minPercentage: number = 0;
  static readonly minAssetValue: number = 1;
  static readonly minScoreCustomerGroup: number = 0;
  static readonly minScoreInvestmentClass: number = 0;
  static readonly minScoreWPServices: number = 0;

  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        CreateSaleManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        CreateSaleManageable.default()
      );
    }
  }

  static readonly required = {
    assetType: true,
    baFinId: true,
    company: true,
    endDate: true,
    fee: true,
    feeEnd: true,
    feeOngoing: true,
    firstPayoutOfInterest: true,
    interestRate: true,
    interestPayoutPeriod: true,
    investmentClass: false,
    maxPurchase: false,
    minPurchase: true,
    percentages: true,
    title: true,
    estimatedValueIncrease: true,
    scoreCustomerGroup: true,
    scoreInvestmentClass: true,
    scoreWPServices: true,
    platformAssetValue: true,
    startDate: true,
    subtitle: true,
    supertitle: true,
    totalAssetValue: true,
    highlights: true,
    description: true,
    descriptionTitle: true,
    predictedPerformance: true,
  };

  static default(): any {
    return {
      ...new CreateSaleInput(),
      assetType: "",
      endDate: "",
      fee: "",
      feeEnd: "",
      feeOngoing: "",
      firstPayoutOfInterest: "",
      interestRate: "",
      interestPayoutPeriod: "",
      investmentClass: InvestmentClass.CRYPTO_CURRENCY,
      minPurchase: "",
      percentages: Object.values(CostCalculationPercentageType).map((type: CostCalculationPercentageType) => new CreateCostCalculationPercentageManageable({ type })),
      estimatedValueIncrease: "",
      scoreCustomerGroup: "",
      scoreInvestmentClass: "",
      scoreWPServices: "",
      platformAssetValue: "",
      startDate: "",
      totalAssetValue: "",
      images: [],
      properties: [{ type: PropertyType.MEDIA }],
      documents: [],
      highlights: ["", "", ""],
      predictedPerformance: "",
      corePropertyRepresentations: [{ properties: [...SaleCoreProperties] }]
    }
  }

  automatic(): any {
    const today = new Date();

    let maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);

    const startDate = new Date(randomDate(
      today,
      maxDate
    ));

    let minEndDate = new Date(Number(startDate));
    minEndDate.setDate(startDate.getDate() + 1);

    const youTubeLink = `<iframe width="640" height="360" src="https://www.youtube.com/embed/4591dCHe_sE" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    const googleMapsLink = `<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15929602.765864901!2d92.43681726315894!3d12.90365926227618!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x304d8df747424db1%3A0x9ed72c880757e802!2sThailand!5e0!3m2!1sen!2srs!4v1643292629339!5m2!1sen!2srs" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy"></iframe>`;

    const customCoreProperties = [randomNoun(), randomNoun()];

    return {
      ...CreateSaleManageable.default(),
      assetType: randomSelectOption('asset'),
      title: randomAdjective() + ' ' + randomNoun(),
      company: randomAdjective() + ' ' + randomNoun(),
      descriptionTitle: randomAdjective() + ' ' + randomNoun(),
      description: Array.from(Array(60).keys()).reduce(
        (result: string) => result + ' ' + randomNoun(),
        ''
      ),
      startDate: dayjs(startDate).toISOString(),
      endDate: dayjs(randomDate(
        minEndDate,
        maxDate
      )).toISOString(),
      firstPayoutOfInterest: dayjs(randomDate(
        minEndDate,
        maxDate
      )).toISOString(),
      minPurchase: 300,
      maxPurchase: undefined,
      scoreCustomerGroup: randomNumber(
        CreateSaleManageable.minScoreCustomerGroup,
        CreateSaleManageable.maxScoreCustomerGroup,
      ),
      scoreInvestmentClass: randomNumber(
        CreateSaleManageable.minScoreInvestmentClass,
        maxScoreInvestmentClass(this.investmentClass),
      ),
      scoreWPServices: randomNumber(
        CreateSaleManageable.minScoreWPServices,
        CreateSaleManageable.maxScoreWPServices,
      ),
      platformAssetValue: randomNumber(
        CreateSaleManageable.minAssetValue + 2000,
        100000
      ),
      subtitle: randomAdjective() + ' ' + randomNoun(),
      supertitle: randomAdjective() + ' ' + randomNoun(),
      totalAssetValue: randomNumber(
        CreateSaleManageable.minAssetValue + 2000,
        100000
      ),
      fee: randomNumber(
        CreateSaleManageable.minFee,
        30
      ),
      feeEnd: randomNumber(
        CreateSaleManageable.minFee,
        30
      ),
      feeOngoing: randomNumber(
        CreateSaleManageable.minFee,
        30
      ),
      interestRate: randomNumber(
        CreateSaleManageable.minInterestRate,
        70
      ),
      percentages: Object.values(CostCalculationPercentageType).map((type: CostCalculationPercentageType) => new CreateCostCalculationPercentageManageable({ type }).automatic()),
      estimatedValueIncrease: randomNumber(
        CreateSaleManageable.minPercentage,
        50
      ),
      predictedPerformance: randomNumber(
        CreateSaleManageable.minPercentage,
        50
      ),
      interestPayoutPeriod: randomSelectOption('period'),
      investmentClass: this.investmentClass,
      baFinId: randomString(6),
      images: this.images,
      documents: this.documents,
      properties: [{
        key: "Listening",
        type: PropertyType.MEDIA,
        value: randomBoolean() ? youTubeLink : googleMapsLink,
      }, {
        key: randomNoun(),
        type: PropertyType.RICH_TEXT,
        value: `<p style="text-align: left;">Here is an image:&nbsp;<img src="https://external-content.duckduckgo.com/iu/?u=https%3A%2F%2F260d5k24r2w64axktwrmh71u-wpengine.netdna-ssl.com%2Fwp-content%2Fuploads%2F2019%2F07%2Fshutterstock_1238044582-1.jpg&amp;f=1&amp;nofb=1" alt="" width="300px"></p><p style="text-align: left;"><br></p><p style="text-align: left;">Some example of&nbsp;<strong>rich text</strong>.</p><p style="text-align: left;">Here is a table:</p><p style="text-align: left;"><br></p>
        <table style="width: 100%;"><tbody>
        <tr>
          <td style="width: 33.3333%;"><br></td>
          <td style="width: 33.3333%;">x</td>
          <td style="width: 33.3333%;">y</td></tr>
        <tr>
          <td style="width: 33.3333%;">V</td>
          <td style="width: 33.3333%;">T</td>
          <td style="width: 33.3333%;">F</td></tr></tbody></table>`
      }, {
        key: customCoreProperties[0],
        type: PropertyType.CORE_PROPERTY,
        value: randomAdjective(),
      }, {
        key: customCoreProperties[1],
        type: PropertyType.CORE_PROPERTY,
        value: randomAdjective(),
      }],
      highlights: [
        randomAdjective() + ' ' + randomNoun(),
        randomAdjective() + ' ' + randomNoun(),
        randomAdjective() + ' ' + randomNoun(),
        randomAdjective() + ' ' + randomNoun(),
      ],
      corePropertyRepresentations: [{ properties: [...SaleCoreProperties] }, { properties: [...customCoreProperties] }]
    };
  }

  validate(
    keys: Array<string>, messages: any
  ): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    validateRequiredNotDefault(
      errors,
      CreateSaleManageable.required,
      this,
      CreateSaleManageable.default(),
      keys
    );

    errors = Object.assign(
      errors,
      validateNumbersInRange(
        [{
          key: 'interestRate',
          min: CreateSaleManageable.minInterestRate,
        }, {
          key: 'fee',
          min: CreateSaleManageable.minFee,
        }, {
          key: 'feeEnd',
          min: CreateSaleManageable.minFee,
        }, {
          key: 'feeOngoing',
          min: CreateSaleManageable.minFee,
        }, {
          key: 'platformAssetValue',
          min: CreateSaleManageable.minAssetValue,
        }, {
          key: 'totalAssetValue',
          min: CreateSaleManageable.minAssetValue,
        }, {
          key: 'minPurchase',
          min: CreateSaleManageable.minMinPurchase,
        }, {
          key: 'maxPurchase',
          min: CreateSaleManageable.minMaxPurchase,
        }, {
          key: 'estimatedValueIncrease',
          min: CreateSaleManageable.minPercentage,
          max: CreateSaleManageable.maxPercentage,
        }, {
          key: 'scoreCustomerGroup',
          min: CreateSaleManageable.minScoreCustomerGroup,
          max: CreateSaleManageable.maxScoreCustomerGroup,
        }, {
          key: 'scoreInvestmentClass',
          min: CreateSaleManageable.minScoreInvestmentClass,
          max: maxScoreInvestmentClass(this.investmentClass),
        }, {
          key: 'scoreWPServices',
          min: CreateSaleManageable.minScoreWPServices,
          max: CreateSaleManageable.maxScoreWPServices,
        }, {
          key: 'predictedPerformance',
          min: CreateSaleManageable.minPercentage,
        }],
        errors,
        this,
        keys,
        messages[ValidationMessages.MINIMUM],
        messages[ValidationMessages.MAXIMUM],
      )
    );

    if (!errors.percentages && shouldValidate('percentages')) {
      const percentagesErrors = this.percentages.map((percentage: CreateCostCalculationPercentageInput) => new CreateCostCalculationPercentageManageable(percentage).validate(
        ["main", "annual"],
        messages
      ));
      const nonEmptyErrors = percentagesErrors.filter((value: any) => !isEmptyObject(value));
      if (nonEmptyErrors.length > 0) {
        errors.percentages = percentagesErrors;
      }
    }

    errors = Object.assign(
      errors,
      validateDates(new CustomValidationArguments({
        keys: ['startDate', 'endDate', 'firstPayoutOfInterest'],
        errors,
        values: this,
        allowedKeys: keys,
        errorMessage: messages[ValidationMessages.REQUIRED]
      }))
    );

    if (!errors.startDate &&
        !errors.endDate &&
        shouldValidate('startDate') &&
        shouldValidate('endDate') &&
        (!dayjs(this.startDate).isBefore(dayjs(this.endDate)) ||
        dayjs().isAfter(this.startDate))) {
      errors.startDate = messages[ValidationMessages.INVALID];
      errors.endDate = messages[ValidationMessages.INVALID];
    }

    if (!errors.startDate &&
        !errors.firstPayoutOfInterest &&
        shouldValidate('startDate') &&
        shouldValidate('firstPayoutOfInterest') &&
        (!dayjs(this.startDate).isBefore(dayjs(this.firstPayoutOfInterest)) ||
        dayjs().isAfter(this.startDate))) {
      errors.startDate = messages[ValidationMessages.INVALID];
      errors.firstPayoutOfInterest = messages[ValidationMessages.INVALID];
    }

    if (!errors.highlights &&
        shouldValidate('highlights')) {
      const minAllowedElementsLength = 3;
      const maxAllowedElementsLength = 4;

      if (this.highlights.length >= minAllowedElementsLength && this.highlights.length <= maxAllowedElementsLength) {
        const highlightMinLength = 10;
        const highlightMaxLength = 30;

        const errorsHighlights = this.highlights.reduce(
          (
            buildingErrors: Array<string> | undefined, highlight: string, index: number
          ) => {
            const lessThanMin = highlight.length < highlightMinLength;
            const greatedThanMax = highlight.length > highlightMaxLength;
            if (lessThanMin || greatedThanMax) {
              if (!buildingErrors) {
                buildingErrors = [];
              }

              let message = lessThanMin ?
                messages[ValidationMessages.MINIMUM] + ' ' + highlightMinLength :
                messages[ValidationMessages.MAXIMUM] + ' ' + highlightMaxLength;

              buildingErrors[index] = message;
            }

            return buildingErrors;
          },
          undefined
        );

        if (errorsHighlights) {
          errors.highlights = errorsHighlights;
        }
      } else {
        errors.highlights = this.highlights.map(() => messages[ValidationMessages.INVALID]);
      }
    }

    return errors;
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    if (dayjs(data.startDate).isValid()) {
      data.startDate = dayjs(data.startDate).toISOString();
    }

    if (dayjs(data.endDate).isValid()) {
      data.endDate = dayjs(data.endDate).toISOString();
    }

    if (dayjs(data.firstPayoutOfInterest).isValid()) {
      data.firstPayoutOfInterest = dayjs(data.firstPayoutOfInterest).toISOString();
    }

    data.assetType = data.assetType.toUpperCase();
    data.interestPayoutPeriod = data.interestPayoutPeriod.toUpperCase();
    data.investmentClass = data.investmentClass.toUpperCase();

    data.fee = parseFloat(data.fee);
    data.feeEnd = parseFloat(data.feeEnd);
    data.feeOngoing = parseFloat(data.feeOngoing);
    data.interestRate = parseFloat(data.interestRate);
    data.estimatedValueIncrease = parseFloat(data.estimatedValueIncrease);
    data.predictedPerformance = parseFloat(data.predictedPerformance);

    data.percentages = data.percentages.map((percentage: CreateCostCalculationPercentageInput) => new CreateCostCalculationPercentageManageable(percentage).serialize());

    data.documents = data.documents.map((document: any) => new CreateFileManageable(document).serialize());
    data.images = data.images.map((image: any) => new CreateFileManageable(image).serialize());
    data.properties = data.properties.filter((property: any) => property.type !== PropertyType.MEDIA || property.key && property.value).map((property: any) => new CreatePropertyManageable(property).serialize());

    return data;
  }
}

export class GetSaleManageable extends GetSaleInput {
  constructor(obj?: any) {
    super();
    if (obj) {
      obj && Object.assign(
        this,
        GetSaleManageable.default(),
        prune(obj)
      );
    } else {
      Object.assign(
        this,
        GetSaleManageable.default()
      );
    }
  }

  static readonly required = {};

  static default(): any {
    return { ...new GetSaleInput() }
  }

  validate(keys: Array<string>): any {
    return validateRequiredNotDefault(
      {},
      CreateSaleManageable.required,
      this,
      CreateSaleManageable.default(),
      keys
    );
  }

  serialize(): any {
    let data: any = Object.assign(
      {},
      this
    );

    return data;
  }
}

export type SaleTypeIntersection =
  | CreateSaleInput
  | GetSaleInput
  | Sale

export type SaleInputTypeUnion =
  & CreateSaleInput
  & GetSaleInput

export type CostCalculationPercentageTypeIntersection =
  | CreateCostCalculationPercentageInputExtended
  | CostCalculationPercentage

export type CostCalculationPercentageInputTypeUnion =
  & CreateCostCalculationPercentageInputExtended